import { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import clsx from 'clsx';
import { generateBase64CoreIcon } from '@corepass/core-blockies';
import { t } from '@ping/helpers';
import { truncateCoreId } from '@ping/utils';
import { useMobile } from '@ping/hooks';

import style from './style.module.scss';

interface IAvatarProps {
  className?: string;
  userCoreID: string;
  tier?: string;
  businessName?: string;
  businessCorePassId?: string;
}

export const Avatar = (props: IAvatarProps) => {
  const { className = '', userCoreID, tier, businessName, businessCorePassId } = props;
  const signedInBusiness = businessName && businessCorePassId ? { businessName, businessCorePassId } : null;
  const isMobile = useMobile();
  const [showTooltip, setShowTooltip] = useState(false);
  const avatarWrapperRef = useRef<HTMLDivElement>(null);

  const isElementFocused = (element: HTMLElement | null) => {
    return element === document.activeElement;
  };

  useEffect(() => {
    const handleFocus = () => {
      setShowTooltip(false);
    };

    avatarWrapperRef.current?.addEventListener('focus', handleFocus);

    return () => {
      // Clean up the event listener when the component unmounts.
      avatarWrapperRef.current?.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleMouseEnter = () => {
    if (!isElementFocused(avatarWrapperRef.current)) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <OverlayTrigger
      show={showTooltip && !isMobile}
      placement='bottom'
      overlay={
        <Tooltip id='avatar-tooltip'>
          {signedInBusiness ? (
            <span className={style['avatar-tooltip']}>
              <span>{signedInBusiness.businessName}</span>
              <span>
                {t('Business')} {signedInBusiness.businessCorePassId}
              </span>
              <span>
                {t('You')} {userCoreID.toUpperCase()}
              </span>
            </span>
          ) : (
            <span>{userCoreID.toUpperCase()}</span>
          )}
        </Tooltip>
      }
    >
      <div
        className={clsx(style['avatar-wrapper'], className)}
        ref={avatarWrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
      >
        <div className={style['avatar-info']}>
          <img className={style['avatar-image']} src={generateBase64CoreIcon(userCoreID, 3)} alt='avatar' />
          {signedInBusiness ? (
            <span className={style['avatar-identity']}>
              <span className={style['avatar-identity__name']}>{signedInBusiness.businessName}</span>
              <span className={style['avatar-identity__line']}>
                <span className={style['avatar-identity__label']}>{t('Business')}</span>
                <span className={style['avatar-identity__id']}>
                  {truncateCoreId(signedInBusiness.businessCorePassId)}
                </span>
              </span>
              <span className={style['avatar-identity__line']}>
                <span className={style['avatar-identity__label']}>{t('You')}</span>
                <span className={style['avatar-identity__id']}>{truncateCoreId(userCoreID.toUpperCase())}</span>
              </span>
            </span>
          ) : (
            <span>{truncateCoreId(userCoreID.toUpperCase())}</span>
          )}
        </div>
        <div className={style['avatar-tier']}>{t(tier?.replace('Tier', 'Tier '))}</div>
      </div>
    </OverlayTrigger>
  );
};
